import React, { useState } from 'react'
import {
  Flex,
  Box,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  Button,
  VStack,
  useDisclosure,
  Badge,
  Input,
  InputGroup,
  InputLeftElement,
  Tag,
  HStack,
  IconButton,
} from '@chakra-ui/react'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import Card from 'components/card/Card'
import { SkeletonTable } from 'components/skeleton/Skeleton'
import { AiOutlineDelete, AiOutlinePlus, AiOutlineSearch } from 'react-icons/ai'
import { MdPercent, MdAttachMoney } from 'react-icons/md'
import { RiCoupon3Line } from 'react-icons/ri'
import { useGetVouchers } from 'queries/voucher.query'
import { CreateVoucherModal } from './CreateVoucherModal'
import { DeleteVoucherModal } from './DeleteVoucherModal'
import { BulkVoucherModal } from './BulkVoucherModal'

const columnHelper = createColumnHelper()

export default function VoucherTable() {
  const [search, setSearch] = useState('')
  const [selectedVoucher, setSelectedVoucher] = useState(null)

  const textColor = useColorModeValue('secondaryGray.900', 'white')
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100')
  const TheadBackground = useColorModeValue('white', 'gray.800')
  const hoverBackgroundColor = useColorModeValue('gray.100', 'gray.700')

  const { isOpen: isCreateOpen, onOpen: onOpenCreate, onClose: onCloseCreate } = useDisclosure()
  const { isOpen: isDeleteOpen, onOpen: onOpenDelete, onClose: onCloseDelete } = useDisclosure()
  const { isOpen: isBulkOpen, onOpen: onOpenBulk, onClose: onCloseBulk } = useDisclosure()

  const { data, isLoading } = useGetVouchers(1, 50, search)

  const vouchersList = React.useMemo(() => {
    if (data?.data) return data.data
    if (Array.isArray(data)) return data
    return []
  }, [data])

  const columns = React.useMemo(
    () => [
      columnHelper.accessor('code', {
        header: 'Promo Code',
        cell: (info) => (
          <HStack spacing={2}>
            <RiCoupon3Line color="#7551FF" size={18} />
            <Text color={textColor} fontWeight="700" fontSize="sm" letterSpacing="1px">
              {info.getValue()}
            </Text>
          </HStack>
        ),
      }),
      columnHelper.accessor('discount_value', {
        header: 'Discount',
        cell: (info) => {
          const type = info.row.original.discount_type
          const val = info.getValue()
          return (
            <Tag size="md" colorScheme={type === 'percent' ? 'blue' : 'green'} display="flex" gap={1}>
              {type === 'percent' ? <MdPercent /> : <MdAttachMoney />}
              <Text fontWeight="700">
                {type === 'percent' ? `${val}%` : `€${val}`}
              </Text>
            </Tag>
          )
        },
      }),
      columnHelper.accessor('used_count', {
        header: 'Usage',
        cell: (info) => {
          const max = info.row.original.max_uses
          return (
            <Text color={textColor} fontSize="sm">
              {info.getValue()} / {max ? max : 'Unlimited'}
            </Text>
          )
        },
      }),
      columnHelper.accessor('event', {
        header: 'Scope',
        cell: (info) => {
          const ev = info.getValue()
          const eventId = info.row.original.event_id
          if (ev?.title) {
            return (
              <Tag size="sm" colorScheme="purple" maxW="240px" isTruncated title={ev.title}>
                {ev.title}
              </Tag>
            )
          }
          if (eventId) {
            return <Tag size="sm" colorScheme="blue">Event #{eventId}</Tag>
          }
          return <Tag size="sm" colorScheme="gray">All Events</Tag>
        },
      }),
      columnHelper.accessor('is_active', {
        header: 'Status',
        cell: (info) => (
          <Badge colorScheme={info.getValue() ? 'green' : 'red'}>
            {info.getValue() ? 'Active' : 'Inactive'}
          </Badge>
        ),
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Actions',
        cell: (info) => (
          <IconButton
            size="sm"
            colorScheme="red"
            variant="ghost"
            icon={<AiOutlineDelete />}
            onClick={() => {
              setSelectedVoucher(info.row.original)
              onOpenDelete()
            }}
          />
        ),
      }),
    ],
    [textColor, onOpenDelete]
  )

  const table = useReactTable({
    data: vouchersList,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  if (isLoading) return <SkeletonTable />

  return (
    <Card flexDirection="column" w="100%" px="0px" overflowX={{ sm: 'scroll', lg: 'hidden' }}>
      <Flex px="25px" mb="20px" justifyContent="space-between" align="center" flexWrap="wrap" gap={3}>
        <VStack align="start" spacing={0}>
          <Text color={textColor} fontSize="22px" fontWeight="700" lineHeight="100%">
            Discounts & Promo Codes
          </Text>
          <Text color="gray.400" fontSize="sm" mt={1}>
            Manage percentage and fixed-amount voucher codes for concert & sports events
          </Text>
        </VStack>

        <Flex gap={3} align="center">
          <InputGroup size="sm" maxW="220px">
            <InputLeftElement pointerEvents="none">
              <AiOutlineSearch color="gray.300" />
            </InputLeftElement>
            <Input
              placeholder="Search voucher code..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              borderRadius="12px"
            />
          </InputGroup>

          <Button
            leftIcon={<AiOutlinePlus />}
            colorScheme="brand"
            size="sm"
            borderRadius="12px"
            onClick={onOpenCreate}
          >
            New Voucher
          </Button>

          <Button
            leftIcon={<RiCoupon3Line />}
            colorScheme="purple"
            variant="outline"
            size="sm"
            borderRadius="12px"
            onClick={onOpenBulk}
          >
            Bulk Generate
          </Button>
        </Flex>
      </Flex>

      <Box overflowX="auto">
        <Table variant="simple" color="gray.500" mb="24px">
          <Thead bg={TheadBackground}>
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Th
                    key={header.id}
                    borderColor={borderColor}
                    py="12px"
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
          <Tbody>
            {table.getRowModel().rows.length === 0 ? (
              <Tr>
                <Td colSpan={columns.length} textAlign="center" py={8}>
                  No vouchers found.
                </Td>
              </Tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <Tr key={row.id} _hover={{ bg: hoverBackgroundColor }}>
                  {row.getVisibleCells().map((cell) => (
                    <Td key={cell.id} borderColor={borderColor} py="14px">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </Td>
                  ))}
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>

      <CreateVoucherModal isOpen={isCreateOpen} onClose={onCloseCreate} />
      <DeleteVoucherModal isOpen={isDeleteOpen} onClose={onCloseDelete} voucher={selectedVoucher} />
      <BulkVoucherModal isOpen={isBulkOpen} onClose={onCloseBulk} />
    </Card>
  )
}
